import { useState } from 'react';

import { ApiError, apiRequest } from '../../api/client.js';
import type { BotSettings, BotTestResult } from '../../api/types.js';
import { Alert } from '../../components/Alert.js';
import { PlatformIcon } from '../../components/PlatformIcon.js';
import { StatusPill } from '../../components/StatusPill.js';
import { Tooltip } from '../../components/Tooltip.js';

/**
 * The Test Console (§102, module brief §25, §42).
 *
 * ==================== IT SAYS "SIMULATION", NEVER "SEND" ====================
 * §42 forbids ambiguous language: the button reads "Run simulation", not "Send
 * test". The difference matters because an operator who believes they are
 * sending will interpret the result as evidence a message went out — and this
 * page's whole purpose is that no message goes out.
 *
 * The banner is permanent rather than shown after a run, and the "not
 * executed" line comes from the SERVER (`providerSend`) rather than being
 * composed here. A sentence about what the server did not do is the server's
 * to write; a Console assembling its own would be free to keep saying it after
 * the behaviour changed.
 * ===========================================================================
 */
export function BotTestConsole({ settings }: { settings: BotSettings }): React.JSX.Element {
  const [platform, setPlatform] = useState(settings.platforms[0]?.platform ?? 'twitch');
  const [message, setMessage] = useState(`${settings.commandPrefix}socials`);
  const [username, setUsername] = useState('testviewer');
  const [badges, setBadges] = useState<string[]>([]);
  const [result, setResult] = useState<BotTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setResult(null);

    try {
      setResult(
        await apiRequest<BotTestResult>('/bot/test', {
          method: 'POST',
          body: {
            platform,
            message,
            username,
            displayName: username,
            platformUserId: 'simulated-viewer',
            badges,
          },
        }),
      );
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'The simulation could not run.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bot__section">
      {/*
       * PERMANENT, not shown after a run. An operator reading a half-loaded
       * page has to be able to tell what this surface does before they use it.
       */}
      <Alert tone="info">
        Simulation only. Nothing here reaches a platform, and no viewer sees anything.
      </Alert>

      <form
        className="bot__form"
        onSubmit={(event) => {
          event.preventDefault();
          void run();
        }}
      >
        <div className="bot__row">
          {/*
           * RADIOS, NOT A SELECT. A native `<option>` cannot carry an icon —
           * browsers render its text only — so a dropdown here would be the
           * one platform picker in the product without a mark. Radios keep the
           * single-choice semantics a select has, and assistive technology
           * reads the group and the chosen option the same way.
           */}
          <fieldset className="bot__badges">
            <legend className="field__label">Platform</legend>
            {settings.platforms.map((entry) => (
              <label className="bot__switch" key={entry.platform}>
                <input
                  type="radio"
                  name="test-platform"
                  checked={platform === entry.platform}
                  onChange={() => {
                    setPlatform(entry.platform);
                  }}
                />
                <PlatformIcon
                  platform={entry.platform}
                  displayName={entry.displayName}
                  size={18}
                />
                <span>{entry.displayName}</span>
              </label>
            ))}
          </fieldset>

          <div className="composer__field">
            <label className="field__label" htmlFor="test-username">
              Viewer
            </label>
            <input
              id="test-username"
              className="field__input"
              value={username}
              onChange={(event) => {
                setUsername(event.target.value);
              }}
            />
          </div>
        </div>

        <fieldset className="bot__badges">
          <legend className="field__label">Roles this viewer has</legend>
          {['subscriber', 'moderator', 'broadcaster'].map((badge) => (
            <label className="bot__switch" key={badge}>
              <input
                type="checkbox"
                checked={badges.includes(badge)}
                onChange={(event) => {
                  setBadges((current) =>
                    event.target.checked
                      ? [...current, badge]
                      : current.filter((entry) => entry !== badge),
                  );
                }}
              />
              <span>{badge}</span>
            </label>
          ))}
        </fieldset>

        <div className="composer__field">
          <label className="field__label" htmlFor="test-message">
            Message
          </label>
          <input
            id="test-message"
            className="field__input"
            value={message}
            onChange={(event) => {
              setMessage(event.target.value);
            }}
          />
        </div>

        <button type="submit" className="btn btn--primary" disabled={busy}>
          {busy ? 'Running…' : 'Run simulation'}
        </button>
      </form>

      {error !== null && <Alert tone="error">{error}</Alert>}

      {result !== null && (
        <div className="bot__result">
          <h2 className="bot__subheading">What would happen</h2>

          <ul className="bot__stages">
            {result.stages.map((stage) => (
              <li className="bot__stage" key={stage.name}>
                <span className="bot__stage-name">{stage.name}</span>
                {/* The word, not only the colour (§65). */}
                <StatusPill
                  tone={stage.passed ? 'ok' : 'warn'}
                  label={stage.passed ? 'Pass' : 'Stopped here'}
                />
                <span className="bot__stage-detail">{stage.detail}</span>
              </li>
            ))}
          </ul>

          {result.targets.length > 0 && (
            <>
              <h3 className="bot__subheading">Where it would go</h3>
              <ul className="bot__stages">
                {result.targets.map((target) => (
                  <li className="bot__stage" key={target.platform}>
                    <PlatformIcon
                      platform={target.platform}
                      displayName={target.displayName}
                      size={18}
                    />
                    <span className="bot__stage-name">{target.displayName}</span>
                    {/*
                     * The capability state travels in the tooltip beside the
                     * verdict. `SUPPORTED` and `REAUTH_REQUIRED` are different
                     * answers with different remedies, and "Skipped" alone
                     * would collapse them.
                     */}
                    <Tooltip tip={target.note ?? `Capability: ${target.capability}.`}>
                      <StatusPill
                        tone={target.eligible ? 'ok' : 'idle'}
                        label={target.eligible ? 'Would send' : (target.skipped ?? 'Skipped')}
                      />
                    </Tooltip>
                    {/* The capability resolver's own sentence, never a paraphrase. */}
                    {target.note !== null && (
                      <span className="bot__stage-detail">{target.note}</span>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}

          <h3 className="bot__subheading">The message</h3>
          {result.renderedResponse === null ? (
            <p className="field__hint">Nothing would be sent.</p>
          ) : (
            /* Untrusted: the operator's own body plus a simulated viewer name,
               rendered as a text node (§21, §57). */
            <p className="bot__preview">{result.renderedResponse}</p>
          )}

          {/* The SERVER's words, rendered as given. */}
          <Alert tone="info">{result.providerSend ?? 'Nothing was sent.'}</Alert>
        </div>
      )}
    </div>
  );
}
